import * as React from 'react';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import {
  getConsultantCurrentlyViewedStep,
  getConsultantTutorialStatus,
  getConsultantTutorialSteps,
} from '../../../reducers/ConsultantTutorialReducer/ConsultantTutorialSelectors';

import { cloneAlphaFromSimulate } from '../../../actions/EditorActions/EditorActions';
import { useConsultantTutorialStepDetails, usePatchConsultantTutorialStep, useTutorialPageContent } from '../../../shared/api/apiHooks';
import { LearnContent } from '../../../components/Learn/LearnDetailsContent/LearnContent';
import Immutable from 'immutable';
import { LearnBlockResponse } from '../../../shared/api/api';

import '../Tutorial.scss';
import { toggleSimulationFilterThunk } from '../../../actions/Simulation/SimulationFiltersActions';
import { FormattedMessageHelper } from '../../../shared/helpers/internationalization';
import analytics from '../../../shared/services/analytics/analytics';
import { Loader } from '../../../components/Loader';
import { getUserPermissionSet } from '../../../reducers/AuthenticationReducer/AuthenticationSelector';
import ConsultantTutorialIndex from './ConsultantTutorialIndex';
import { Icon } from 'semantic-ui-react';
import { ConsultantTutorialActions } from '../../../reducers/ConsultantTutorialReducer/ConsultantTutorialReducer';
import { ConsultantTutorialButtons } from './ConsultantTutorialButtons';
import { TutorialTask } from '../TutorialTask';

interface LearnProps {
  fullWidth: boolean;
  currentActiveTab: number;
  isMultiTab: boolean;
  currentChildActiveTab: number;
  checkIfTabIsEmptyCode: (tabIndex: number, tabIndexChild?: number | undefined) => boolean;
}

type LearnSimulateGeneric = LearnProps & Pick<RouteComponentProps, 'history'>;

interface TutorialScreenProps {
  currentActiveTab: number;
  isMultiTab: boolean;
  currentChildActiveTab: number;
  checkIfTabIsEmptyCode: (tabIndex: number, tabIndexChild?: number | undefined) => boolean;
  history: any;
}

export function TutorialContent({
  currentActiveTab,
  isMultiTab,
  currentChildActiveTab,
  checkIfTabIsEmptyCode,
  history,
}: TutorialScreenProps) {
  const dispatch = useDispatch();
  // Use both React Query for API calls and Redux for state
  const { isFetching: isFetchingSteps } = useConsultantTutorialStepDetails();

  // Get data from Redux store
  const steps = useSelector(getConsultantTutorialSteps);
  const isTutorialComplete = useSelector(getConsultantTutorialStatus) === 'COMPLETED';
  const currentStep = useSelector(getConsultantCurrentlyViewedStep);
  const isTutorialStarted =
    currentStep !== 0 || steps.filter(step => step.status === 'COMPLETED' || step.status === 'IN_PROGRESS').length > 0;
  const tutorialPageSlug = steps?.[currentStep]?.slug;
  const { data: tutorialContent } = useTutorialPageContent(tutorialPageSlug);

  const immutableContent = Immutable.fromJS(tutorialContent?.content ?? []) as Immutable.List<LearnBlockResponse>;

  const handleSimulateClone = useCallback(
    (alpha: any) => {
      const childActiveTab = isMultiTab ? currentChildActiveTab : -1;
      dispatch(cloneAlphaFromSimulate(alpha.toJS(), currentActiveTab, childActiveTab));
    },
    [currentActiveTab, currentChildActiveTab, dispatch, isMultiTab]
  );

  const handleShowModal = useCallback(() => {
    const childActiveTab = isMultiTab ? currentChildActiveTab : -1;
    let showModal;

    if (childActiveTab !== -1) {
      showModal = !checkIfTabIsEmptyCode(currentActiveTab, childActiveTab);
    } else {
      showModal = !checkIfTabIsEmptyCode(currentActiveTab);
    }

    return showModal;
  }, [checkIfTabIsEmptyCode, currentActiveTab, currentChildActiveTab, isMultiTab]);

  const handleFinishTutorial = () => {
    analytics.trackCustomEvent('consultant_tutorial_click_finish');
    // @ts-expect-error thunk dispatch argument type mismatch
    dispatch(toggleSimulationFilterThunk('learn', currentActiveTab, history));
  };

  const { mutate: updateStep } = usePatchConsultantTutorialStep();

  const handleStepClick = (stepId: number) => {
    // Update the current step when a step is clicked
    updateStep(stepId);
    // Track the event for analytics
    analytics.trackCustomEvent('consultant_tutorial_step_click', { event_label: stepId.toString() });
  };

  const handleStartTutorial = () => {
    analytics.trackCustomEvent('consultant_tutorial_click_start');
    updateStep(0);
  };

  const consultantTutorialStep = steps[currentStep];
  const isStepComplete = consultantTutorialStep?.status === 'COMPLETED';
  const currentTask = consultantTutorialStep?.task;
  if (!isTutorialStarted) {
    return <ConsultantTutorialStartPage onStart={handleStartTutorial} />;
  }

  if (isTutorialComplete) {
    return <ConsultantTutorialFinishPage onFinish={handleFinishTutorial} />;
  }

  return (
    <>
      {isFetchingSteps && steps.length === 0 ? (
        <Loader size={'massive'} />
      ) : (
        <>
          <ConsultantTutorialIndex currentStepId={currentStep} onStepClick={handleStepClick} />
          {currentTask && <TutorialTask task={currentTask} isComplete={isStepComplete} />}
          {tutorialContent?.title && <h1>{tutorialContent.title}</h1>}
          {tutorialContent ? (
            <LearnContent item={immutableContent} handleSimulateClone={handleSimulateClone} handleShowModal={handleShowModal} />
          ) : (
            <Loader size={'massive'} />
          )}
          {currentTask && <TutorialTask task={currentTask} isComplete={isStepComplete} />}
        </>
      )}
      <ConsultantTutorialButtons />
    </>
  );
}

export const ConsultantTutorial = ({
  fullWidth,
  currentActiveTab,
  isMultiTab,
  currentChildActiveTab,
  checkIfTabIsEmptyCode,
  history,
}: LearnSimulateGeneric) => {
  const permissionSet = useSelector(getUserPermissionSet);

  const isConsultant = permissionSet.has('CONSULTANT');

  const renderContent = React.useCallback(() => {
    if (!isConsultant) {
      return null;
    }

    return (
      <>
        <TutorialContent
          currentActiveTab={currentActiveTab}
          isMultiTab={isMultiTab}
          currentChildActiveTab={currentChildActiveTab}
          checkIfTabIsEmptyCode={checkIfTabIsEmptyCode}
          history={history}
        />
      </>
    );
  }, [currentActiveTab, isMultiTab, currentChildActiveTab, checkIfTabIsEmptyCode, history]);

  return (
    <div className={`tutorial-simulate ${fullWidth ? 'tutorial-simulate--full-width' : ''}`}>
      <div className="tutorial-background">{renderContent()}</div>
    </div>
  );
};

interface ConsultantTutorialFinishPageProps {
  onFinish: () => void;
}

export function ConsultantTutorialFinishPage({ onFinish }: ConsultantTutorialFinishPageProps) {
  const dispatch = useDispatch();
  const handleFinishTutorial = () => {
    dispatch(ConsultantTutorialActions.finishTutorial());
    onFinish();
  };

  //todo: add confetti after user tutorial chapters are merged
  return (
    <div className="tutorial-start">
      <h1>
        <Icon name="book" color="green" />
        <FormattedMessageHelper id="consultantTutorial.congratulations" />
      </h1>
      <p>
        <FormattedMessageHelper id="consultantTutorial.youHaveCompleted" />
      </p>
      <p>
        <FormattedMessageHelper id="consultantTutorial.certificateIssued" />
      </p>
      <div className="tutorial-start__button-container">
        <button className="ui button primary" onClick={handleFinishTutorial}>
          <FormattedMessageHelper tagName="span" id="consultantTutorial.closeTutorial" />
        </button>
      </div>
    </div>
  );
}

interface ConsultantTutorialStartPageProps {
  onStart: () => void;
}

export function ConsultantTutorialStartPage({ onStart }: ConsultantTutorialStartPageProps) {
  const handleStartTutorial = () => {
    onStart();
  };

  //todo: add confetti after user tutorial chapters are merged
  return (
    <div className="tutorial-start">
      <h1>
        <Icon name="book" color="green" />
        <FormattedMessageHelper id="consultantTutorial.startTitle" />
      </h1>
      <p>
        <FormattedMessageHelper id="consultantTutorial.startText1" />
      </p>
      <p>
        <FormattedMessageHelper id="consultantTutorial.startText2" />
      </p>
      <p>
        <FormattedMessageHelper id="consultantTutorial.startText3" />
      </p>
      <p>
        <FormattedMessageHelper id="consultantTutorial.startText4" />
      </p>
      <p>
        <FormattedMessageHelper id="consultantTutorial.startText5" />
      </p>
      <div className="tutorial-start__button-container">
        <button className="ui button primary" onClick={handleStartTutorial}>
          <FormattedMessageHelper tagName="span" id="navigatorTutorial.startTutorial" />
        </button>
      </div>
    </div>
  );
}
